import React from 'react';
import PropTypes from 'prop-types';
import Dropdown from '../CommonComponents/Dropdown';
import Icon from '../CommonComponents/Icon';
import { EXTERNAL_HOST } from '../ConsumerArea/ConsumerAreaConstants';
import { getGoalPageName } from '../FinancialPlanOPL/Goals/utils/getGoalPageName';

const getCustomIcon = g => {
  if (g.GoalOrigin === 'PulseCheck') {
    return 'pulseCheck';
  }

  return null;
};

const parseOptions = (options, currentOption) =>
  options.reduce((acc, goal) => {
    if (goal.GoalId === currentOption && !goal.DebtOutputPerType) {
      return acc;
    }
    if (goal.DebtOutputPerType) {
      const debtOutputs = goal.DebtOutputPerType.map(output => ({
        label: output.TabLabel,
        value: output.DebtType,
        isSubGoal: true,
        parentGoalId: goal.GoalId,
        icon: `goal--dropdown__icon ${goal.icon}`,
      }));

      return [
        ...acc,
        {
          label: goal.GoalName,
          value: goal.GoalId,
          type: goal.GoalType,
          icon: `goal--dropdown__icon ${goal.icon}`,
        },
      ].concat(debtOutputs);
    }

    return [
      ...acc,
      {
        label: goal.GoalName,
        value: goal.GoalId,
        type: goal.GoalType,
        icon: getCustomIcon(goal) || `goal--dropdown__icon ${goal.icon}`,
        goalType: goal.GoalType,
      },
    ];
  }, []);

const FinancialPlanGoalDropdownView = ({
  clientId,
  goals,
  goalId,
  setGoalId,
  onGoalChanged,
  selectedGoalName,
  setSubGoalType,
  externalHost,
  displayAssetBucketing,
  assetBucketingHeadline,
}) => {
  if (externalHost === EXTERNAL_HOST.ELEMENT) {
    return <h2 className="goal-summary__title">{selectedGoalName}</h2>;
  }

  const items = parseOptions(goals, goalId);

  if (displayAssetBucketing) {
    items.unshift({
      label: assetBucketingHeadline,
      value: 'assetBucketing',
      isAssetBucketing: true,
      icon: 'far fa-envelope-open-dollar',
    });
  }

  return (
    <Dropdown
      onChange={option => {
        if (option.isAssetBucketing) {
          ADV.util.Router.go2('AssetBucketingResults', {
            clientId,
            year: new Date().getFullYear(),
          });
        } else if (option.value === 'summary') {
          ADV.util.Router.go2('GoalsSummary', { clientId });
        } else if (option.isSubGoal) {
          ADV.util.Router.go2(getGoalPageName(option.type), {
            clientId,
            goalId: option.parentGoalId,
            workflowEnded: false,
            subGoalTypeId: option.value,
          });
          setSubGoalType(option.value, clientId);
        } else {
          setSubGoalType('', clientId);
          setGoalId(option.value, clientId, onGoalChanged, option.goalType);
        }
      }}
      items={items}
    >
      <h2 className="goal-summary__dropdown-container">
        {selectedGoalName}
        <Icon size="tiny" className="fas fa-chevron-down" />
      </h2>
    </Dropdown>
  );
};

FinancialPlanGoalDropdownView.defaultProps = {
  externalHost: null,
};

FinancialPlanGoalDropdownView.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  goals: PropTypes.arrayOf(
    PropTypes.shape({
      GoalName: PropTypes.string,
      GoalId: PropTypes.string,
      icon: PropTypes.string,
    }),
  ).isRequired,
  goalId: PropTypes.string.isRequired,
  setGoalId: PropTypes.func.isRequired,
  onGoalChanged: PropTypes.func.isRequired,
  selectedGoalName: PropTypes.string.isRequired,
  setSubGoalType: PropTypes.func.isRequired,
  externalHost: PropTypes.string,
  displayAssetBucketing: PropTypes.bool.isRequired,
  assetBucketingHeadline: PropTypes.string.isRequired,
};

export default FinancialPlanGoalDropdownView;
